import type {
  ProjectTrackingPlan,
  SuggestedTrackingPlan,
  WebsiteDiscovery,
  WebsiteResearch,
} from '@shared/api-client';
import {
  MAX_ONBOARDING_GOALS,
  customTrackingGoals,
  type TrackingGoal,
} from '@shared/util/tracking-plan';
import { ArrowRight, Check, Plus } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { WebsiteResearchSummary } from '@/components/website-research';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  allowedRevenueProviders,
  normalizeRevenueSelection,
} from '@/lib/onboarding-integrations';

type GoalDraft = TrackingGoal & {
  id: number;
  selected: boolean;
  eventEdited: boolean;
  evidence?: string;
  reason?: string;
  sourceUrl?: string;
  measurement?: 'outcome' | 'proxy';
};
const goalDrafts = (
  plan?: ProjectTrackingPlan | SuggestedTrackingPlan | null,
): GoalDraft[] =>
  customTrackingGoals(plan ?? undefined).map((goal, id) => ({
    ...goal,
    id,
    selected: true,
    eventEdited: true,
  }));
const hasSuggestions = (plan?: SuggestedTrackingPlan | null) =>
  Boolean(
    plan &&
    (plan.signups || plan.customGoals.length || plan.revenue !== 'none'),
  );
const detailClass =
  'ml-7 text-sm text-ink-muted [&_summary]:absolute [&_summary]:right-4 [&_summary]:top-3 [&_summary]:cursor-pointer [&_summary]:rounded [&_summary]:py-1 [&_summary]:font-medium [&_summary]:hover:text-ink [&_summary]:focus-visible:outline-accent';

export const TrackingChoices = ({
  initial,
  suggested,
  research,
  onSuggest,
  resumeAnalysis = false,
  analysisProgress,
  platform,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  resumeAnalysis?: boolean;
  analysisProgress?: ReactNode;
  initial?: ProjectTrackingPlan;
  suggested?: SuggestedTrackingPlan | null;
  research?: WebsiteResearch | null;
  onSuggest?: () => Promise<
    Pick<
      WebsiteDiscovery,
      'suggestedTrackingPlan' | 'warnings' | 'websiteResearch'
    >
  >;
  platform: 'web' | 'mobile' | 'both';
  pending: boolean;
  error?: string;
  onSubmit: (plan: ProjectTrackingPlan) => void;
  onCancel?: () => void;
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  const source = initial ?? suggested;
  const mobile = platform === 'mobile';
  const revenueProviders = allowedRevenueProviders(platform);
  const [recommendation, setRecommendation] = useState(suggested);
  const [reviewedResearch, setReviewedResearch] = useState(research);
  const [signups, setSignups] = useState(source?.signups ?? false);
  const [goals, setGoals] = useState(() => goalDrafts(source));
  const [openGoal, setOpenGoal] = useState<number | 'signups' | null>(null);
  const nextId = useRef(goals.length);
  const [revenue, setRevenue] = useState<ProjectTrackingPlan['revenue']>(
    normalizeRevenueSelection(platform, source?.revenue ?? 'none'),
  );
  const [formError, setFormError] = useState('');
  const [scanWarning, setScanWarning] = useState('');
  const needsSuggestions =
    (resumeAnalysis || (!initial && suggested === undefined && !research)) &&
    Boolean(onSuggest);
  const [scanPending, setScanPending] = useState(needsSuggestions);
  const automaticScanStarted = useRef(false);
  const editVersion = useRef(0);
  const busy = pending;
  const selectedCount =
    Number(signups) + goals.filter((goal) => goal.selected).length;
  const atLimit = selectedCount >= MAX_ONBOARDING_GOALS;
  const recommended = hasSuggestions(recommendation);
  const updateGoal = (id: number, changes: Partial<GoalDraft>) => {
    editVersion.current += 1;
    setGoals((previous) =>
      previous.map((goal) => (goal.id === id ? { ...goal, ...changes } : goal)),
    );
  };
  const suggest = useCallback(async () => {
    if (!onSuggest) {
      return;
    }
    automaticScanStarted.current = true;
    const version = editVersion.current;
    setScanPending(true);
    setFormError('');
    setScanWarning('');
    try {
      const discovery = await onSuggest();
      const result = discovery.suggestedTrackingPlan ?? null;
      setScanWarning(discovery.warnings.join(' '));
      setRecommendation(result);
      setReviewedResearch(discovery.websiteResearch ?? null);
      if (editVersion.current !== version) {
        return;
      }
      if (
        result &&
        (result.signups ||
          result.customGoals.length ||
          result.revenue !== 'none')
      ) {
        setSignups(result.signups);
        const draft = goalDrafts(result);
        setGoals(draft);
        nextId.current = draft.length;
        setRevenue(normalizeRevenueSelection(platform, result.revenue));
      } else {
        if (!discovery.warnings.length) {
          setScanWarning(
            'We couldn’t find clear conversion goals on this website. You can add your own below.',
          );
        }
      }
    } catch {
      setFormError(
        'We couldn’t scan your website. Try again or choose your goals below.',
      );
    } finally {
      setScanPending(false);
    }
  }, [onSuggest, platform]);
  useEffect(() => {
    if (needsSuggestions && !automaticScanStarted.current) {
      automaticScanStarted.current = true;
      void suggest();
    }
  }, [needsSuggestions, suggest]);
  return (
    <form
      className="space-y-6"
      onChange={() => {
        editVersion.current += 1;
      }}
      onSubmit={(event) => {
        event.preventDefault();
        if (busy) {
          return;
        }
        const selected = goals
          .filter((goal) => goal.selected)
          .map(({ name, eventName, trigger }) => ({
            name: name.trim(),
            eventName: eventName.trim(),
            trigger: trigger.trim(),
          }));
        if (mobile && !signups && !selected.length && revenue === 'none') {
          setFormError('Choose at least one thing to set up for your app.');
          return;
        }
        if (selectedCount > MAX_ONBOARDING_GOALS) {
          setFormError(
            `Choose up to ${MAX_ONBOARDING_GOALS} goals, including signups.`,
          );
          return;
        }
        const events = selected.map((goal) => goal.eventName.toLowerCase());
        if (
          events.includes('signup_completed') ||
          new Set(events).size !== events.length
        ) {
          setFormError(
            'Use a different event name for each goal. Use the Signups option for signup_completed.',
          );
          return;
        }
        setFormError('');
        editVersion.current += 1;
        onSubmit({
          signups,
          customGoal: selected[0] ?? null,
          customGoals: selected,
          revenue,
        });
      }}
      onInvalidCapture={(event) => {
        const details = (event.target as HTMLElement).closest('details');
        if (details) {
          details.open = true;
        }
      }}
    >
      <div>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl font-semibold tracking-tight text-ink outline-none"
        >
          {scanPending ? 'Preparing your setup…' : 'Here’s what we’ll track'}
        </h1>
        <p className="mt-3 text-base leading-relaxed text-ink-muted">
          {scanPending
            ? 'You can adjust your goals while we finish.'
            : 'Confirm your goals, then let your coding agent connect them.'}
        </p>
        {!initial &&
          (!recommended || scanWarning) &&
          onSuggest &&
          !scanPending && (
            <Button
              type="button"
              variant="secondary"
              className="mt-4"
              loading={scanPending}
              disabled={pending}
              onClick={() => void suggest()}
            >
              {scanPending ? 'Preparing goals…' : 'Retry goal analysis'}
            </Button>
          )}
      </div>
      {analysisProgress}
      {scanWarning && <Alert tone="warning">{scanWarning}</Alert>}
      <fieldset disabled={busy} className="space-y-5">
        <legend className="sr-only">Tracking choices</legend>
        {!mobile && (
          <div className="flex items-start gap-3 py-1">
            <Check className="mt-0.5 size-4 shrink-0 text-accent" />
            <div>
              <p className="text-sm font-semibold text-ink">
                Website activity{' '}
                <span className="ml-2 text-xs font-normal text-ink-muted">
                  Included
                </span>
              </p>
              <p className="mt-1 text-sm text-ink-muted">
                Pageviews & traffic sources
              </p>
            </div>
          </div>
        )}
        <section aria-labelledby="conversion-goals-title">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2
                  id="conversion-goals-title"
                  className="text-sm font-semibold text-ink"
                >
                  Goals
                </h2>
                <span
                  className="rounded-md bg-surface-muted px-2 py-1 text-xs text-ink-muted"
                  aria-live="polite"
                >
                  {selectedCount} / {MAX_ONBOARDING_GOALS} selected
                </span>
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={atLimit || goals.length >= MAX_ONBOARDING_GOALS}
              onClick={() => {
                editVersion.current += 1;
                const id = nextId.current++;
                setGoals((previous) => [
                  ...previous,
                  {
                    id,
                    name: '',
                    eventName: '',
                    trigger: '',
                    selected: true,
                    eventEdited: false,
                  },
                ]);
                setOpenGoal(id);
              }}
            >
              <Plus /> Add goal
            </Button>
          </div>
          <div className="mt-4 divide-y divide-border overflow-hidden rounded-xl border border-border">
            <div className="relative px-4 py-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={signups}
                  disabled={!signups && atLimit}
                  onChange={(event) => {
                    setSignups(event.target.checked);
                    if (!event.target.checked) {
                      setOpenGoal((current) =>
                        current === 'signups' ? null : current,
                      );
                    }
                  }}
                  className="mt-1 size-4 shrink-0 accent-accent disabled:opacity-40"
                />
                <span>
                  <span className="block pr-24 text-sm font-semibold text-ink">
                    Signups
                  </span>
                  <span className="mt-1 block text-sm text-ink-muted">
                    Account created
                  </span>
                </span>
              </label>
              <details
                className={detailClass}
                open={openGoal === 'signups'}
                onToggle={(event) => {
                  const isOpen = event.currentTarget.open;
                  setOpenGoal((current) =>
                    isOpen ? 'signups' : current === 'signups' ? null : current,
                  );
                }}
              >
                <summary>
                  Details
                  <span className="sr-only"> for Signups</span>
                </summary>
                <div className="mt-3 space-y-2 border-t border-border pt-3 leading-relaxed">
                  <p>
                    Counts completed registrations, including social signups.
                    Your agent connects each signup to the right user.
                  </p>
                  <p>
                    Event: <code>signup_completed</code>
                  </p>
                  {recommendation?.signupEvidence && (
                    <p>
                      Suggested from your website: “
                      {recommendation.signupEvidence}”
                    </p>
                  )}
                </div>
              </details>
            </div>
            {goals.map((goal) => (
              <div key={goal.id} className="relative px-4 py-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={goal.selected}
                    disabled={!goal.selected && atLimit}
                    onChange={(event) => {
                      updateGoal(goal.id, { selected: event.target.checked });
                      if (!event.target.checked) {
                        setOpenGoal((current) =>
                          current === goal.id ? null : current,
                        );
                      }
                    }}
                    className="mt-1 size-4 shrink-0 accent-accent disabled:opacity-40"
                  />
                  <span className="min-w-0">
                    <span className="block break-words pr-24 text-sm font-semibold text-ink">
                      {goal.name || 'New goal'}
                    </span>
                    <span className="mt-1 line-clamp-2 break-words text-sm text-ink-muted">
                      {goal.trigger ||
                        'Add a name and when this goal should count.'}
                    </span>
                  </span>
                </label>
                {goal.measurement === 'proxy' && (
                  <p className="ml-7 mt-1 text-xs text-ink-muted">
                    Intent only · does not confirm a sale
                  </p>
                )}
                <details
                  className={detailClass}
                  open={openGoal === goal.id}
                  onToggle={(event) => {
                    const isOpen = event.currentTarget.open;
                    setOpenGoal((current) =>
                      isOpen ? goal.id : current === goal.id ? null : current,
                    );
                  }}
                >
                  <summary>
                    Details
                    <span className="sr-only"> {goal.name || 'new goal'}</span>
                  </summary>
                  <fieldset
                    disabled={!goal.selected}
                    className="mt-4 space-y-4 border-t border-border pt-4"
                  >
                    <Field label="Goal name" htmlFor={`goal-name-${goal.id}`}>
                      <Input
                        id={`goal-name-${goal.id}`}
                        value={goal.name}
                        maxLength={80}
                        required
                        onChange={(event) => {
                          const name = event.target.value;
                          updateGoal(goal.id, {
                            name,
                            ...(!goal.eventEdited
                              ? {
                                  eventName: name
                                    .toLowerCase()
                                    .replace(/[^a-z0-9]+/g, '_')
                                    .replace(/^_|_$/g, '')
                                    .slice(0, 100),
                                }
                              : {}),
                          });
                        }}
                      />
                    </Field>
                    <Field
                      label="When should it count?"
                      htmlFor={`goal-trigger-${goal.id}`}
                    >
                      <Input
                        id={`goal-trigger-${goal.id}`}
                        value={goal.trigger}
                        maxLength={300}
                        required
                        onChange={(event) =>
                          updateGoal(goal.id, { trigger: event.target.value })
                        }
                      />
                    </Field>
                    <Field
                      label="Event name"
                      htmlFor={`goal-event-${goal.id}`}
                      hint="Your agent will use this exact name. We’ll create the matching goal."
                      className="[&_p]:text-ink-muted"
                    >
                      <Input
                        id={`goal-event-${goal.id}`}
                        value={goal.eventName}
                        maxLength={100}
                        pattern="[A-Za-z0-9][A-Za-z0-9_.:\-]*"
                        required
                        className="font-mono"
                        onChange={(event) =>
                          updateGoal(goal.id, {
                            eventName: event.target.value,
                            eventEdited: true,
                          })
                        }
                      />
                    </Field>
                  </fieldset>
                  {goal.reason && (
                    <p className="mt-3 leading-relaxed">{goal.reason}</p>
                  )}
                  {goal.sourceUrl && (
                    <a
                      className="mt-3 inline-block text-accent underline underline-offset-4"
                      href={goal.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Website evidence
                      <span className="sr-only"> for {goal.name}</span>
                    </a>
                  )}
                  {goal.evidence && (
                    <p className="mt-3 leading-relaxed">
                      Suggested from your website: “{goal.evidence}”
                    </p>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    className="mt-3 rounded py-1 text-critical hover:underline disabled:opacity-50"
                    onClick={() => {
                      editVersion.current += 1;
                      setGoals((previous) =>
                        previous.filter((item) => item.id !== goal.id),
                      );
                      setOpenGoal(null);
                    }}
                  >
                    Remove goal<span className="sr-only"> {goal.name}</span>
                  </button>
                </details>
              </div>
            ))}
          </div>
        </section>
        <section aria-labelledby="revenue-title">
          <h2 id="revenue-title" className="sr-only">
            Revenue
          </h2>
          <div className="relative rounded-xl border border-border px-4 py-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={revenue !== 'none'}
                onChange={(event) =>
                  setRevenue(event.target.checked ? 'undecided' : 'none')
                }
                className="mt-1 size-4 shrink-0 accent-accent"
              />
              <span>
                <span className="block pr-24 text-sm font-semibold text-ink">
                  Paid customers & revenue
                </span>
                <span className="mt-1 block text-sm text-ink-muted">
                  From your billing provider
                </span>
              </span>
            </label>
            {revenue !== 'none' && (
              <details className={detailClass}>
                <summary>Settings</summary>
                <div className="mt-4 space-y-3 border-t border-border pt-4">
                  <Field
                    label="How do you collect payments?"
                    htmlFor="setup-revenue"
                  >
                    <select
                      id="setup-revenue"
                      value={revenue}
                      onChange={(event) =>
                        setRevenue(
                          event.target.value as ProjectTrackingPlan['revenue'],
                        )
                      }
                      className="h-10 w-full rounded-lg border border-border-strong bg-surface px-3 text-sm text-ink"
                    >
                      <option value="undecided">
                        Let my agent check the project
                      </option>
                      {revenueProviders.includes('stripe') && (
                        <option value="stripe">Stripe</option>
                      )}
                      {revenueProviders.includes('revenuecat') && (
                        <option value="revenuecat">RevenueCat</option>
                      )}
                      {revenueProviders.length === 2 && (
                        <option value="both">Stripe and RevenueCat</option>
                      )}
                    </select>
                  </Field>
                  <p className="leading-relaxed">
                    Your agent prepares the code and identity mapping. You
                    connect the provider securely in GainSignal.
                  </p>
                  {recommendation?.revenueEvidence && (
                    <p>
                      Suggested from your website: “
                      {recommendation.revenueEvidence}”
                    </p>
                  )}
                </div>
              </details>
            )}
          </div>
        </section>
      </fieldset>
      <WebsiteResearchSummary research={reviewedResearch} />
      {mobile && selectedCount > 0 && (
        <p className="text-sm leading-relaxed text-ink-muted">
          Your agent will check native event support before connecting app
          activity. The website script works only in a browser.
        </p>
      )}
      {(error || formError) && <Alert tone="error">{error || formError}</Alert>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {onCancel ? (
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel changes
          </Button>
        ) : (
          <p className="text-xs text-ink-muted">
            You can change these choices later.
          </p>
        )}
        <Button type="submit" size="lg" loading={pending}>
          Continue to agent setup <ArrowRight />
        </Button>
      </div>
    </form>
  );
};
